import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Flex, Form, Modal, Select, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import getOutboundPricingHistory, { IOutboundPricingVersion } from '../../../../../../domain/use_cases/admins/getOutboundPricingHistory';
import { EFieldType } from '../../../../../../domain/enums/admin/AdminFieldType';
import CallOutboundPricingTable from './CallOutboundPricingTable';

interface CallOutboundPricingHistoryModalProps {
  adminId: string;
  open: boolean;
  onClose: () => void;
}

const DAY = 'YYYY-MM-DD';

/**
 * The grid as it was at a chosen date: the picker only opens the days that hold a version, a
 * second list picks the time when a day holds several. Read-only, nothing is restored from here.
 */
const CallOutboundPricingHistoryModal: React.FC<CallOutboundPricingHistoryModalProps> = ({ adminId, open, onClose }) => {
  const [versions, setVersions] = useState<IOutboundPricingVersion[] | null>(null);
  const [day, setDay] = useState<Dayjs | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);

  useEffect(() => {
    // Mounted on open only, so the state starts empty on every opening
    if (!open) return;
    getOutboundPricingHistory(adminId)
      .then((loaded) => {
        setVersions(loaded);
        // Lands on the latest version, the one in production right now
        const latest = loaded[0];
        setDay(latest ? dayjs(latest.created) : null);
        setVersionId(latest?.id ?? null);
      })
      .catch(() => setVersions([]));
  }, [adminId, open]);

  const daysWithVersions = useMemo(() => new Set((versions ?? []).map((version) => dayjs(version.created).format(DAY))), [versions]);
  const versionsOfDay = useMemo(
    () => (versions ?? []).filter((version) => day && dayjs(version.created).format(DAY) === day.format(DAY)),
    [versions, day],
  );
  const version = versionsOfDay.find((candidate) => candidate.id === versionId) ?? versionsOfDay[0] ?? null;

  return (
    <Modal title="Historique de la grille" open={open} onCancel={onClose} footer={null} width={1100} destroyOnHidden>
      <Flex gap={8} wrap="wrap" style={{ marginBottom: 12 }}>
        <DatePicker
          value={day}
          onChange={(picked) => { setDay(picked); setVersionId(null); }}
          disabledDate={(candidate) => !daysWithVersions.has(candidate.format(DAY))}
          format="DD/MM/YYYY"
          placeholder="Date"
        />
        <Select
          value={version?.id ?? null}
          onChange={(picked) => setVersionId(picked)}
          options={versionsOfDay.map((candidate) => ({ value: candidate.id, label: `${dayjs(candidate.created).format('HH:mm')} · ${candidate.author || 'auteur inconnu'} · ${candidate.version}` }))}
          placeholder="Heure"
          style={{ width: 320 }}
          disabled={versionsOfDay.length === 0}
        />
        <Typography.Text type="secondary">
          {versions === null ? 'Chargement...' : `${versions.length} version${versions.length > 1 ? 's' : ''} enregistrée${versions.length > 1 ? 's' : ''}`}
        </Typography.Text>
      </Flex>

      {version && (
        // Its own form, so the table reads this version and never the one being edited behind
        <Form key={version.id} initialValues={{ grid: version.value }} layout="vertical">
          <CallOutboundPricingTable
            type={EFieldType.CUSTOM}
            namePath={['grid']}
            label={`Grille du ${dayjs(version.created).format('DD/MM/YYYY à HH:mm')} · version ${version.version}`}
            value={version.value}
            isReadOnly
          />
        </Form>
      )}
    </Modal>
  );
};

export default CallOutboundPricingHistoryModal;